// components/chat/PainelContato.js — a mini-página do contato no chat
// (out/26, redesign "Chat Cetto.dc.html", telas 1b e 1g).
//
// Abre pelo botão de pessoa no topo da conversa e ocupa a MESMA coluna da
// direita do painel de templates (um de cada vez). No celular é uma folha
// que sobe de baixo. Substitui o popover "i" (só anotações) e o botão
// "Abrir no CRM" do topo: tudo o que o atendente precisava ir ao CRM pra ver
// no meio da conversa — dados, etiquetas e pastas, de qual anúncio veio,
// anotações — fica a um clique, e o CRM continua no botão de baixo.
//
// Só LÊ o contato (GET /contacts/{id} e /notes) — editar é no CRM. As
// etiquetas são a exceção: chegam prontas do ChatPage (`etiquetas`), com o
// mesmo popover de sempre, porque é lá que vive a lógica de marcar.
//
// Contato EXCLUÍDO no CRM ainda tem conversa no chat; o GET dá 404 e a
// mini-página mostra o que a lista já sabe, sem "Abrir no CRM" (que não
// acharia o contato).
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Drawer, Tooltip } from '@mui/material';
import { FiX, FiCopy, FiCheck, FiExternalLink, FiTarget, FiFolder } from 'react-icons/fi';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/Api';
import { formatarFone } from '../../pages/UserAdmin/crm/theme';
import { STATUS, descrever } from '../qualificacao/regras';

const SYNE = "'Syne', sans-serif";

const rotuloSecao = {
  fontSize: 11.5, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
  color: 'var(--ch-cap)', m: '0 0 10px 4px',
};

function ondeVeio(o) {
  if (o?.rede === 'instagram') return 'Veio do anúncio no Instagram';
  if (o?.rede === 'facebook') return 'Veio do anúncio no Facebook';
  return 'Veio de um anúncio';
}

const dataCurta = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'dd/MM/yyyy', { locale: ptBR });
};

function Linha({ rotulo, children, ultima }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '12px', minHeight: 46, px: '14px',
      borderBottom: ultima ? 'none' : '1px solid var(--ch-bdr)',
    }}>
      <Box component="span" sx={{ fontSize: 13.5, color: 'var(--ch-cap)', flex: 1, whiteSpace: 'nowrap' }}>{rotulo}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, fontSize: 14, color: 'var(--ch-text)' }}>
        {children}
      </Box>
    </Box>
  );
}

function Conteudo({ cliente, avatar, folha, etiquetas, detalhe, notas, carregando, excluido }) {
  const [copiado, setCopiado] = useState(false);
  const fone = formatarFone(cliente.phone_number);
  const perfil = (cliente.whatsapp_name || '').trim();
  const qual = detalhe?.qualificacao || (cliente.lead_status ? { status: cliente.lead_status } : null);
  const st = qual?.status ? STATUS[qual.status] : null;
  const pastas = detalhe?.folders || cliente.folders || [];
  const origem = detalhe?.origem_anuncio;
  const nome = cliente.name || fone;
  const inicial = (cliente.name || '?')[0]?.toUpperCase();

  const copiar = () => {
    try {
      navigator.clipboard.writeText(`+${String(cliente.phone_number || '').replace(/\D/g, '')}`);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1600);
    } catch { /* sem permissão de área de transferência */ }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '26px' }}>
      {/* Quem é */}
      <Box sx={folha
        ? { display: 'flex', alignItems: 'center', gap: '14px' }
        : { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '4px' }}>
        <Box sx={{
          width: folha ? 64 : 72, height: folha ? 64 : 72, borderRadius: '50%', flexShrink: 0,
          bgcolor: avatar.bg, color: avatar.color, mb: folha ? 0 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: SYNE, fontSize: folha ? 25 : 28, fontWeight: 800,
        }}>
          {inicial}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: folha ? 'flex-start' : 'center', gap: '3px', minWidth: 0 }}>
          <Box sx={{ fontFamily: SYNE, fontSize: folha ? 19 : 20, fontWeight: 700, letterSpacing: '-0.01em', color: 'var(--ch-text)', overflowWrap: 'anywhere' }}>
            {nome}
          </Box>
          <Box sx={{ fontSize: 14, color: 'var(--ch-text2)', fontVariantNumeric: 'tabular-nums' }}>{fone}</Box>
          {st && (
            <Box sx={{
              mt: folha ? '6px' : '10px', display: 'inline-flex', alignItems: 'center', gap: '7px',
              height: folha ? 26 : 28, px: '12px', borderRadius: 999,
              bgcolor: st.bg, border: `1px solid ${st.borda}`, color: st.cor, fontSize: 13, fontWeight: 600,
            }}>
              <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: st.ponto }} />
              {qual.status === 'qualificado' ? 'Lead qualificado' : descrever(qual) || st.rotulo}
            </Box>
          )}
        </Box>
      </Box>

      {excluido && (
        <Box sx={{
          p: '10px 12px', borderRadius: '12px', fontSize: 13, lineHeight: 1.5,
          bgcolor: 'var(--ch-amber-bg)', border: '1px solid var(--ch-amber-b)', color: 'var(--ch-amber-d)',
        }}>
          Este contato foi excluído do CRM. A conversa continua aqui.
        </Box>
      )}

      {/* Dados */}
      <Box>
        <Box sx={rotuloSecao}>Dados</Box>
        <Box sx={{ bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)', borderRadius: '14px', overflow: 'hidden' }}>
          <Linha rotulo="Telefone">
            <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fone}</Box>
            <Tooltip title={copiado ? 'Copiado' : 'Copiar número'}>
              <Box component="button" type="button" onClick={copiar} aria-label="Copiar número"
                sx={{
                  display: 'flex', p: '4px', m: '-4px', border: 0, bgcolor: 'transparent', borderRadius: '6px', cursor: 'pointer',
                  color: copiado ? 'var(--ch-green-d)' : 'var(--ch-cap)', '&:hover': { color: 'var(--ch-text)' },
                  '&:focus-visible': { outline: '2px solid var(--ch-green)' },
                }}>
                {copiado ? <FiCheck size={14} /> : <FiCopy size={14} />}
              </Box>
            </Tooltip>
          </Linha>
          {perfil && (
            <Linha rotulo="Nome no WhatsApp">
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>~{perfil}</Box>
            </Linha>
          )}
          <Linha rotulo="Responsável" ultima>
            {carregando && !detalhe ? (
              <CircularProgress size={12} sx={{ color: 'var(--ch-cap)' }} />
            ) : detalhe?.assigned_user_name ? (
              <>
                <Box component="span" sx={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  bgcolor: 'var(--ch-av3-bg)', color: 'var(--ch-av3-fg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: SYNE, fontSize: 10, fontWeight: 700,
                }}>
                  {detalhe.assigned_user_name[0]?.toUpperCase()}
                </Box>
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detalhe.assigned_user_name}</Box>
              </>
            ) : (
              <Box component="span" sx={{ color: 'var(--ch-cap)' }}>Sem responsável</Box>
            )}
          </Linha>
        </Box>
      </Box>

      {/* Etiquetas e pastas */}
      <Box>
        <Box sx={rotuloSecao}>Etiquetas e pastas</Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
          {etiquetas}
          {pastas.map((p) => (
            <Box key={p.id} component="span" sx={{
              height: 28, display: 'inline-flex', alignItems: 'center', gap: '6px', px: '11px',
              borderRadius: '8px', fontSize: 13, fontWeight: 500,
              bgcolor: 'var(--ch-inset)', color: 'var(--ch-text)', border: '1px solid var(--ch-bdr)',
            }}>
              <FiFolder size={13} color="var(--ch-blue)" aria-hidden />
              {p.name}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Origem: só quando veio de anúncio */}
      {origem && (
        <Box>
          <Box sx={rotuloSecao}>Origem</Box>
          <Box sx={{ bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)', borderRadius: '14px', p: '14px', display: 'flex', gap: '12px' }}>
            <Box component="span" sx={{
              width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: 'var(--ch-purple-bg)', color: 'var(--ch-purple-d)', border: '1px solid var(--ch-purple-b)',
            }}>
              <FiTarget size={16} aria-hidden />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ch-purple-d)' }}>{ondeVeio(origem)}</Box>
              {origem.titulo && <Box component="span" sx={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ch-text)' }}>{origem.titulo}</Box>}
              {origem.texto && !folha && (
                <Box component="span" sx={{
                  fontSize: 13.5, lineHeight: 1.5, color: 'var(--ch-text2)',
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {origem.texto}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px', mt: '4px' }}>
                {origem.chegou_em && (
                  <Box component="span" sx={{ fontSize: 13, color: 'var(--ch-cap)', fontVariantNumeric: 'tabular-nums' }}>
                    Chegou em {dataCurta(origem.chegou_em)}
                  </Box>
                )}
                {origem.link && (
                  <Box component="a" href={origem.link} target="_blank" rel="noopener noreferrer"
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 13.5, fontWeight: 600,
                      color: 'var(--ch-purple-d)', textDecoration: 'none', '&:hover': { textDecoration: 'underline' },
                    }}>
                    Ver anúncio <FiExternalLink size={13} aria-hidden />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Anotações */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', m: '0 0 10px 4px' }}>
          <Box component="span" sx={{ ...rotuloSecao, m: 0 }}>Anotações</Box>
          {notas.length > 0 && (
            <Box component="span" sx={{ fontSize: 12, color: 'var(--ch-cap)', fontVariantNumeric: 'tabular-nums' }}>{notas.length}</Box>
          )}
        </Box>
        {carregando && notas.length === 0 ? (
          <CircularProgress size={14} sx={{ color: 'var(--ch-cap)', ml: '4px' }} />
        ) : notas.length === 0 ? (
          <Box sx={{ fontSize: 13.5, color: 'var(--ch-cap)', ml: '4px', lineHeight: 1.5 }}>
            Nenhuma anotação ainda.{!excluido && ' Elas são escritas no CRM.'}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {notas.map((n) => (
              <Box key={n.id} sx={{
                bgcolor: 'var(--ch-surf)', border: '1px solid var(--ch-bdr)', borderRadius: '14px',
                p: '12px 14px', display: 'flex', flexDirection: 'column', gap: '4px',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' }}>
                  <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ch-text)' }}>{n.author_name || '—'}</Box>
                  <Box component="span" sx={{ fontSize: 12, color: 'var(--ch-cap)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {n.created_at ? format(new Date(n.created_at), "dd MMM · HH:mm", { locale: ptBR }) : ''}
                  </Box>
                </Box>
                <Box sx={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ch-text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {n.text}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function PainelContato({ cliente, avatar, modo = 'coluna', aberto = true, onFechar, onAbrirCrm, etiquetas }) {
  const [detalhe, setDetalhe] = useState(null);
  const [notas, setNotas] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [excluido, setExcluido] = useState(false);
  const id = cliente?.id;
  const visivel = Boolean(id) && aberto;

  useEffect(() => {
    if (!visivel) return undefined;
    let vivo = true;
    setDetalhe(null); setNotas([]); setExcluido(false); setCarregando(true);
    Promise.allSettled([api.get(`/contacts/${id}`), api.get(`/contacts/${id}/notes`)]).then(([d, n]) => {
      if (!vivo) return;
      if (d.status === 'fulfilled') setDetalhe(d.value.data || null);
      else if (d.reason?.response?.status === 404) setExcluido(true);
      if (n.status === 'fulfilled') setNotas(Array.isArray(n.value.data) ? n.value.data : []);
      setCarregando(false);
    });
    return () => { vivo = false; };
  }, [id, visivel]);

  if (!cliente) return null;
  const folha = modo === 'folha';

  const cabecalho = (
    <Box sx={folha
      ? { height: 52, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flexShrink: 0 }
      : { height: 68, pl: '20px', pr: '14px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid var(--ch-bdr)', flexShrink: 0 }}>
      <Box component="h2" sx={{ m: 0, flex: folha ? 'none' : 1, fontFamily: SYNE, fontSize: folha ? 16 : 15, fontWeight: 700, color: 'var(--ch-text)' }}>
        Contato
      </Box>
      <Box component="button" type="button" onClick={onFechar} aria-label="Fechar contato"
        sx={{
          ...(folha ? { position: 'absolute', right: 14, top: 8, width: 36, height: 36 } : { width: 32, height: 32 }),
          p: 0, border: 0, borderRadius: '50%', cursor: 'pointer', bgcolor: 'var(--ch-inset)', color: 'var(--ch-text2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          '&:hover': { color: 'var(--ch-text)' },
          '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
        }}>
        <FiX size={folha ? 16 : 15} strokeWidth={2.4} />
      </Box>
    </Box>
  );

  const rodape = !excluido && (
    <Box sx={{ p: folha ? '12px 18px calc(16px + env(safe-area-inset-bottom))' : '14px 20px 18px', borderTop: '1px solid var(--ch-bdr)', flexShrink: 0 }}>
      <Box component="button" type="button" onClick={() => onAbrirCrm(cliente.id)}
        sx={{
          width: '100%', height: folha ? 50 : 44, border: 0, borderRadius: 999, cursor: 'pointer',
          bgcolor: 'var(--ch-green)', color: 'var(--ch-sobre-verde)', fontFamily: 'inherit',
          fontSize: folha ? 16 : 14.5, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          boxShadow: '0 10px 24px -12px rgba(0,160,95,0.6)',
          '&:hover': { bgcolor: 'var(--ch-green-hover)' },
          '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 3 },
        }}>
        Abrir no CRM <FiExternalLink size={folha ? 16 : 15} aria-hidden />
      </Box>
    </Box>
  );

  const corpo = (
    <Box sx={{
      flex: 1, overflowY: 'auto', p: folha ? '10px 18px 20px' : '24px 20px 28px',
      '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { background: 'var(--ch-bdr2)', borderRadius: 3 },
    }}>
      <Conteudo cliente={cliente} avatar={avatar} folha={folha} etiquetas={etiquetas}
        detalhe={detalhe} notas={notas} carregando={carregando} excluido={excluido} />
    </Box>
  );

  if (folha) {
    return (
      <Drawer
        anchor="bottom" open={aberto} onClose={onFechar}
        slotProps={{ backdrop: { sx: { bgcolor: 'var(--ch-veu)' } } }}
        PaperProps={{
          'aria-label': `Contato: ${cliente.name || cliente.phone_number}`,
          sx: {
            height: '88dvh', bgcolor: 'var(--ch-card)', color: 'var(--ch-text)', backgroundImage: 'none',
            borderRadius: '28px 28px 0 0', boxShadow: '0 -20px 50px -10px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Outfit', sans-serif",
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '8px', flexShrink: 0 }}>
          <Box component="span" sx={{ width: 36, height: 5, borderRadius: 999, bgcolor: 'var(--ch-bdr2)' }} />
        </Box>
        {cabecalho}
        {corpo}
        {rodape}
      </Drawer>
    );
  }

  return (
    <Box component="aside" aria-label="Contato" sx={{
      width: 340, minWidth: 340, bgcolor: 'var(--ch-card)', borderLeft: '1px solid var(--ch-bdr)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {cabecalho}
      {corpo}
      {rodape}
    </Box>
  );
}
